import * as React from "react";

export interface PromptOptions {
  title?: string;
  message: string;
  defaultValue?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
  /** 多行文字(例如要使用者手動複製的內容);此時 Enter 換行,Ctrl/⌘+Enter 送出 */
  multiline?: boolean;
}

type PromptFn = (options: PromptOptions | string) => Promise<string | null>;

interface PendingPrompt extends PromptOptions {
  resolve: (value: string | null) => void;
}

const PromptContext = React.createContext<PromptFn | null>(null);

const INPUT_CLASS =
  "mt-3 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-300";

export const PromptProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = React.useState<PendingPrompt | null>(null);
  const [value, setValue] = React.useState("");
  const pendingRef = React.useRef<PendingPrompt | null>(null);
  const inputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const prompt = React.useCallback<PromptFn>((options) => {
    const opts = typeof options === "string" ? { message: options } : options;
    return new Promise<string | null>((resolve) => {
      // 若已有未回應的對話框,視為取消
      pendingRef.current?.resolve(null);
      const next = { ...opts, resolve };
      pendingRef.current = next;
      setValue(opts.defaultValue ?? "");
      setPending(next);
    });
  }, []);

  const settle = React.useCallback((result: string | null) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    current?.resolve(result);
  }, []);

  React.useEffect(() => {
    if (!pending) return;
    // 開啟後聚焦並全選,方便直接覆蓋或複製
    inputRef.current?.focus();
    inputRef.current?.select();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // 用 capture 搶先處理,避免同時關掉底下的 modal
        e.stopImmediatePropagation();
        settle(null);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [pending, settle]);

  return (
    <PromptContext.Provider value={prompt}>
      {children}
      {pending && (
        <div
          className="fixed inset-0 z-[210] flex items-center justify-center bg-black/40 p-4"
          onClick={() => settle(null)}
        >
          <form
            role="dialog"
            aria-modal="true"
            aria-label={pending.title ?? pending.message}
            className="max-h-[90vh] w-full max-w-sm overflow-y-auto rounded-2xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              settle(value);
            }}
          >
            {pending.title && <h2 className="mb-2 text-base font-semibold text-slate-900">{pending.title}</h2>}
            <p className="whitespace-pre-wrap text-sm text-slate-600">{pending.message}</p>
            {pending.multiline ? (
              <textarea
                ref={(el) => {
                  inputRef.current = el;
                }}
                rows={8}
                value={value}
                placeholder={pending.placeholder}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    settle(value);
                  }
                }}
                className={INPUT_CLASS}
              />
            ) : (
              <input
                ref={(el) => {
                  inputRef.current = el;
                }}
                type="text"
                value={value}
                placeholder={pending.placeholder}
                onChange={(e) => setValue(e.target.value)}
                className={INPUT_CLASS}
              />
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => settle(null)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                {pending.cancelText ?? "取消"}
              </button>
              <button
                type="submit"
                className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                {pending.confirmText ?? "確認"}
              </button>
            </div>
          </form>
        </div>
      )}
    </PromptContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export function usePrompt(): PromptFn {
  const ctx = React.useContext(PromptContext);
  if (!ctx) throw new Error("usePrompt 必須在 PromptProvider 內使用");
  return ctx;
}
